import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ComponentRendering,
  Field,
  Placeholder,
  PlaceholderComponentProps,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { SitecoreContextValue } from '@sitecore-jss/sitecore-jss-react/types/components/SitecoreContext';
import classNames from 'classnames';

import {
  calculateDarkerHex,
  ComponentProps,
  getContrastThemeClass,
  isColorBright,
  resolveBackgroundStyle,
  resolveColorProperty,
  useFallbackTranslation,
} from '@gn-audio/jss-components';

import { Eyebrow } from 'common/components/Eyebrow';
import { useSpotTracking } from 'utils/hooks/useSpotTracking';

import DesktopTabNavigation from './DesktopTabNavigation';
import MobileTabNavigation from './MobileTabNavigation';
import TabNavigationContentItem from './TabNavigationContentItem';
import { TabNavigationItemFields } from './tabNavigationSpotTypes';

type TabNavigationSpot = {
  placeholderKey: Field<string>;
  eyebrowText: Field<string>;
  header: Field<string>;
  subHeader: Field<string>;
  tabs: TabNavigationItemFields[];
  activeTab: Field<string>;
  isFullWidth: Field<boolean>;
  textAlignment: Field<'center' | 'left'>;
  background: Field<string>;
  spotBackground: Field<string>;
  trackingSpotName?: Field<string>;
  ariaLabel: Field<string>;
};

type TabNavigationSpotProps = {
  fields: TabNavigationSpot;
  sitecoreContext: SitecoreContextValue;
} & ComponentProps;

const generateColorScheme = (color: string) => {
  const resolvedColor = resolveColorProperty(color) || '#ffffff';
  const darkerResolvedColor = calculateDarkerHex(resolvedColor, 0.2);
  return {
    '--tab-bg-color': resolvedColor,
    '--tab-text-color': isColorBright(resolvedColor) ? 'black' : 'white',
    '--tab-darker-bg-color': darkerResolvedColor,
    '--tab-darker-text-color': isColorBright(darkerResolvedColor) ? 'black' : 'white',
  } as React.CSSProperties;
};

let tabNavigationInstanceCounter = 0;

const TabNavigationSpot = ({ fields, sitecoreContext }: TabNavigationSpotProps) => {
  const { t } = useFallbackTranslation();

  const instanceIdRef = useRef<number | undefined>(undefined);
  if (instanceIdRef.current === undefined) {
    instanceIdRef.current = ++tabNavigationInstanceCounter;
  }
  const instanceId = instanceIdRef.current;

  const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null);
  const { route } = sitecoreContext;

  const {
    activeTab,
    isFullWidth,
    header,
    subHeader,
    textAlignment,
    eyebrowText,
    background,
    spotBackground,
    tabs,
    placeholderKey,
    trackingSpotName,
    ariaLabel,
  } = fields;

  const spotRef = useSpotTracking(trackingSpotName?.value);

  const baseLabel = header?.value || eyebrowText?.value || subHeader?.value;
  const navigationLabel = baseLabel || `${t('system.Navigation')} ${instanceId}`;

  const isTextCentered = textAlignment?.value === 'center';
  const textAlignmentClass = isTextCentered ? 'text-center' : 'text-left';
  const eyebrowAlignment = (isTextCentered ? 'center' : 'left') as 'center' | 'left';

  const navigationType = useMemo(() => {
    if (!tabs?.length) return null;

    if (tabs.every((tab) => 'icon' in tab && !!tab.icon?.value)) {
      return 'icon';
    } else if (
      tabs.every((tab) => 'image' in tab && !!Object.keys(tab?.image?.value || {}).length)
    ) {
      return 'image';
    } else if (tabs.every((tab) => 'text' in tab && !!tab.text?.value)) {
      return 'text';
    } else {
      return null;
    }
  }, [tabs]);

  useEffect(() => {
    if (!activeTab?.value || !tabs?.length) return;

    const activeIndex = tabs.findIndex((tab) => tab.name === activeTab?.value);
    if (activeIndex === -1) return;

    setActiveTabIndex(activeIndex);
  }, [activeTab?.value, tabs]);

  const colorScheme = generateColorScheme(background?.value);
  const spotBackgroundStyle = resolveBackgroundStyle(spotBackground?.value) || {
    backgroundColor: 'transparent',
  };
  const spotThemeClass = getContrastThemeClass(spotBackground?.value as string);
  const hasText = header?.value || subHeader?.value || eyebrowText?.value;
  const sectionStyle = {
    ...colorScheme,
    ...spotBackgroundStyle,
  };

  const handleTabClick = useCallback(
    (index: number) => {
      if (activeTabIndex === index) return;

      setActiveTabIndex(index);
    },
    [activeTabIndex]
  );

  const renderPlaceholder = (name: string, render?: PlaceholderComponentProps['render']) => {
    return route?.placeholders[name] ? (
      <Placeholder name={name} rendering={route} render={render} disableSuspense />
    ) : null;
  };

  return (
    <section
      ref={spotRef}
      data-wt-spot={trackingSpotName?.value}
      style={sectionStyle}
      className="spot"
    >
      <div className="container">
        {hasText && (
          <div className={classNames(textAlignmentClass, 'mx-auto mb-10 max-w-6xl')}>
            <Eyebrow
              text={eyebrowText?.value}
              align={eyebrowAlignment}
              className={classNames(spotThemeClass, 'mb-6')}
            />
            {header?.value && (
              <h2 className={classNames(spotThemeClass, 'heading-2')}>{header.value}</h2>
            )}
            {subHeader?.value && (
              <p className={classNames(spotThemeClass, 'paragraph-large')}>{subHeader.value}</p>
            )}
          </div>
        )}

        {tabs?.length > 0 && (
          <div role="navigation" aria-label={navigationLabel}>
            {!navigationType && (
              <div className="mb-4 bg-primary p-4" role="alert">
                <p className="font-bold">Tab Navigation Setup Warning</p>
                <p>
                  This tab navigation is not set up correctly. All tabs types should be either text,
                  icons, or images. Defaulting to text navigation.
                </p>
              </div>
            )}
            <DesktopTabNavigation
              navigationType={navigationType || 'text'}
              tabs={tabs}
              activeIndex={activeTabIndex}
              handleTabClick={handleTabClick}
              isFullWidth={isFullWidth?.value}
              instanceId={instanceId}
              ariaLabel={ariaLabel?.value}
            />

            <MobileTabNavigation
              navigationType={navigationType || 'text'}
              tabs={tabs}
              activeIndex={activeTabIndex}
              handleTabClick={handleTabClick}
              instanceId={instanceId}
              ariaLabel={ariaLabel?.value}
            />
          </div>
        )}
      </div>
      <div>
        <>
          {tabs?.length > 0 && (
            <>
              {renderPlaceholder(
                placeholderKey?.value,
                (components: ReactElement[], renderings: ComponentRendering[]) => (
                  <div className="flex flex-col">
                    {tabs.map(({ content, id }, tabIndex) => {
                      if (!content?.length) return null;

                      const isActiveTab = activeTabIndex === tabIndex;
                      const tabClassName = isActiveTab ? 'block' : 'hidden';

                      const tabContentItems = content.map(({ id: contentDataSourceId }) => {
                        const matchedRenderingIndex = renderings.findIndex(
                          (rendering: ComponentRendering) =>
                            rendering.dataSource === contentDataSourceId
                        );
                        if (matchedRenderingIndex === -1) return null;

                        const matchedRendering = renderings[matchedRenderingIndex];
                        const matchedComponent = components[matchedRenderingIndex];
                        const isJsonRendering = 'componentName' in matchedRendering;
                        if (!isJsonRendering) return matchedComponent;

                        return (
                          <div key={contentDataSourceId} className="flex flex-col">
                            <TabNavigationContentItem
                              component={matchedComponent}
                              rendering={matchedRendering as ComponentRendering}
                            />
                          </div>
                        );
                      });

                      return (
                        <div
                          key={`tab-content-${id}`}
                          className={tabClassName}
                          aria-hidden={!isActiveTab}
                          role="tabpanel"
                          aria-labelledby={`tab-${instanceId}-${tabIndex}`}
                          id={`tabpanel-${instanceId}-${tabIndex}`}
                        >
                          {tabContentItems}
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </>
          )}
        </>
      </div>
    </section>
  );
};

export const getStaticProps = async ({ fields }: ComponentRendering<TabNavigationSpot>) => {
  const tabs = fields?.tabs;
  if (!fields || !tabs?.length) return {};

  tabs.forEach((tab) => {
    const tabContent = tab?.content;
    if (!tabContent?.length) return;

    tab.content = tabContent.map((contentDataSource) => ({
      id: `{${contentDataSource?.id}}`.toUpperCase(),
    }));
  });

  return {};
};

export default withDatasourceCheck()(TabNavigationSpot);
